/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext } from 'react';
import { ExpandMoreRounded, Search } from '@mui/icons-material';
import {
  Box, Divider, Grid, MenuItem, Pagination, Select, Skeleton, Snackbar, Stack, TextField,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import Images from '../../../../Configs/images';
import Elements from '../../../Elements';
import ROUTES from '../../../../Configs/routes';
import { getRequestedItems } from '../../../../Redux/Models/market';
import AlertContext from '../../../../Context/AlertContext';
import { OverviewCard } from '../Overview/Overview';
import { formatNumToCurrency } from '../../../../Utils/helpers';

export default function Market({
  data, classes, error, message, isLoading, totalPage = 1, currentPage = 1,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState('Newest');
  const [keyword, setKeyword] = useState('');
  const [column, setColumn] = useState(2);

  const { openAlert } = useContext(AlertContext);

  const getData = () => {
    dispatch(getRequestedItems({
      limit, page, keyword,
    }));
  };

  useEffect(() => {
    getData();
  }, [limit, page]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (page === 1) {
        getData();
      } else {
        setPage(1);
      }
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [keyword]);

  useEffect(() => {
    if (error) {
      openAlert({
        message: `Something went wrong. ${message}`,
        severity: 'error',
      });
    }
  }, [error]);

  const renderItems = () => {
    if (isLoading) {
      return (
        <Grid container spacing={2}>
          {new Array(3).fill(0).map((l, idx) => (
            <Grid item xs={12 / column} key={idx}>
              <Box display="flex" gap={1.5} border="1px solid #E5E5E5" borderRadius="5px" p={2}>
                <Skeleton variant="rectangular" height={80} width={80} />
                <Box flex={1}>
                  <Box className="weight-600">
                    <Skeleton variant="text" width="80%" />
                  </Box>
                  <Box display="flex" className="s-10" color="#586479" gap={0.5} flexWrap="wrap" alignItems="center" mt={0.25}>
                    <Skeleton variant="text" width="100%" />
                    <Skeleton variant="text" width="80%" />
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                    <Box className="s-16 weight-700">
                      <Skeleton variant="text" width="50%" />
                    </Box>
                    <Box display="flex" gap={0.5}>
                      <Skeleton variant="text" width="20%" />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      );
    }
    if (data.length) {
      return (
        <Grid container spacing={2}>
          { data.map((item, index) => (
            <Grid item xs={12 / column} key={item.id}>
              <Box display="flex" gap={1.5} onClick={() => navigate(`${ROUTES.market()}/detail/${item.id}`)} key={index} border="1px solid #E5E5E5" borderRadius="5px" p={2}>
                <img
                  width="80px"
                  height="80px"
                  className="rounded cover"
                  src={item.photo}
                  alt="product"
                />
                <Box flex={1}>
                  <Box className="weight-600">{item.name}</Box>
                  <Box display="flex" className="s-10" color="#586479" gap={0.5} flexWrap="wrap" alignItems="center" mt={0.25}>
                    <div>{item.category_name || '-'}</div>
                  </Box>

                  <Box fontSize={12} mt={2} fontWeight="600">
                    Status:
                    {' '}
                    {item.status}
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      );
    }

    return (
      <div className="s-16 mt-5 center">
        <img src={Images.Sure} alt="No data" />
        <div className="weight-700 mt-4">Market items will appear in here soon</div>
        <div className="mt-3">Check again later</div>
      </div>
    );
  };

  return (
    <Elements.AdminPageBase navigate={navigate}>
      <div className={classes.root}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box fontSize={20}>
            <strong>Requested Items</strong>
          </Box>
        </Box>

        <Box mt={2} className="flex gap-16 space-between">
          <TextField
            placeholder="Search by keyword"
            size="small"
            sx={{
              ' .MuiInputBase-root': {
                bgcolor: '#fff',
              },
            }}
            value={keyword}
            onChange={({ target }) => setKeyword(target.value)}
            InputProps={{
              startAdornment: (<Search fontSize="small" sx={{ mr: 1, color: '#D4D4D4' }} />),
            }}
          />
          <Box display="flex" gap={2}>
            <Select
              select
              size="small"
              value={sort}
              onChange={({ target }) => setSort(target.value)}
              IconComponent={ExpandMoreRounded}
              sx={{
                borderRadius: '10px',
                bgcolor: '#fff',
              }}
            >
              <MenuItem value="Oldest">Oldest</MenuItem>
              <MenuItem value="Newest">Newest</MenuItem>
            </Select>
            <Select
              select
              size="small"
              value={limit}
              onChange={({ target }) => setLimit(target.value)}
              IconComponent={ExpandMoreRounded}
              sx={{
                borderRadius: '10px',
                bgcolor: '#fff',
              }}
            >
              <MenuItem value={10}>10 List</MenuItem>
              <MenuItem value={20}>20 List</MenuItem>
              <MenuItem value={50}>50 List</MenuItem>
            </Select>
          </Box>
        </Box>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Box fontSize={12} color="#586479">
            <strong>{data.length}</strong>
            {' '}
            {data.length > 1 ? 'items' : 'item'}
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Pagination
              count={totalPage}
              variant="outlined"
              shape="rounded"
              size="small"
              page={!isLoading ? currentPage : page}
              siblingCount={0}
              onChange={(e, newPage) => setPage(newPage)}
            />
            <Divider orientation="vertical" flexItem />
            <Box display="flex" alignItems="center" gap={1}>
              {[2, 3].map((val) => (
                <Box
                  key={val}
                  border={val !== column ? '1px solid #E5E5E5' : ''}
                  bgcolor={val === column ? '#38AC3D' : 'none'}
                  boxSizing="border-box"
                  borderRadius="5px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width={24}
                  height={24}
                  color={val === column ? '#fff' : '#000'}
                  fontSize={12}
                  className="link"
                  onClick={() => setColumn(val)}
                >
                  {val}
                </Box>
              ))}
              <Box color="#404852" fontWeight="600" fontSize={14}>Columns</Box>
            </Box>
          </Box>
        </Box>

        <Box mt={2} bgcolor="#FDFDFD" borderRadius="10px" p={2}>
          {renderItems()}
        </Box>
      </div>
    </Elements.AdminPageBase>
  );
}
